import lodash from 'lodash';
import { ActionCreators as UndoActions } from 'redux-undo';
import { v4 } from 'uuid';
import documentSlice from './slice';
import * as Arrows from './arrowCalc';

const getSolutionConnectorProps = doc => {
  const connectors = doc.solutionModel.signals;
  const allConnectorPropsUnsorted = {};
  const allConnectorPropsSorted = {};
  const allConnectorTerminalsSorted = {};
  const allConnectorTerminalsUnsorted = {};
  let i = 0;
  for (const connector in connectors) {
    const connectorProps = [
      connectors[connector].polarity,
      connectors[connector].connectFrom,
      connectors[connector].connectTo,
    ];
    const connectorTerminals = connectorProps.slice(1);
    allConnectorTerminalsUnsorted[i] = JSON.stringify(connectorTerminals);
    connectorTerminals.sort();
    allConnectorPropsUnsorted[i] = JSON.stringify(connectorProps);
    connectorProps.sort();
    allConnectorPropsSorted[i] = JSON.stringify(connectorProps);
    allConnectorTerminalsSorted[i] = JSON.stringify(connectorTerminals);
    i++;
  }
  return {
    sorted: allConnectorPropsSorted,
    unsorted: allConnectorPropsUnsorted,
    sortedTerminals: allConnectorTerminalsSorted,
    unsortedTerminals: allConnectorTerminalsUnsorted,
  };
};

export const loadModel = ({ doc, fileInfo }) => dispatch => {
  // initalize the status of duplicate name to be false
  doc.duplicateName = false;
  // initialize labelBankItems and allLabelBankItems
  let labelOrder = Object.keys(doc.labels);
  if (doc.labelOrder.startsWith('fixed:')) {
    labelOrder = doc.labelOrder.substr(6).split(',');
  } else if (doc.labelOrder === 'random') {
    labelOrder = lodash.shuffle(labelOrder);
  } else {
    labelOrder.sort((a, b) => doc.labels[a].localeCompare(doc.labels[b]));
  }
  doc.allLabelBankItems = labelOrder;
  doc.labelBankItems = labelOrder.filter(
    key => !doc.workingModel || !(key in doc.workingModel.nodes),
  );

  // if workingModel is empty, initialize it from initialModel
  if (
    !doc.editMode &&
    (!doc.workingModel ||
      lodash.isEqual(doc.workingModel, { nodes: {}, signals: [] }))
  ) {
    if (doc.initialmodel) {
      doc.workingModel = lodash.cloneDeep(doc.initialModel);
    } else {
      doc.workingModel = { nodes: {}, signals: [] };
    }
  }
  // calculate arrow positions
  Arrows.init(doc);
  //initialize solutionConnectorProps, solutionModelTotalObjectsCount,
  //connectedSolutionObjects, and solutionModelTotalFlowObjectsCount if solution exists
  if (doc?.solutionModel) {
    // isolate connectObjects from decoy objects
    const connectedObjectKeys = new Set();
    let solutionModelTotalFlowObjectsCount = 0;
    //find which objects are connected via signals
    doc.solutionModel.signals.forEach(signal => {
      connectedObjectKeys.add(signal.connectFrom);
      connectedObjectKeys.add(signal.connectTo);
    });
    //find which objects are connected via flows
    for (const [key, value] of Object.entries(doc.solutionModel.nodes)) {
      let thisFlowConnected = false;
      if (value.connectFrom) {
        connectedObjectKeys.add(value.connectFrom);
        thisFlowConnected = true;
      }
      if (value.connectTo) {
        connectedObjectKeys.add(value.connectTo);
        thisFlowConnected = true;
      }
      if (thisFlowConnected) {
        connectedObjectKeys.add(key);
        solutionModelTotalFlowObjectsCount++;
      }
    }
    // we're only counting connected objects in solutionModelTotalObjectsCount,
    // rather than all objects, which would include "decoys"
    doc.solutionModelTotalObjectsCount = connectedObjectKeys.size;

    const connectedSolutionObjects = lodash.flow([
      Object.entries,
      arr => arr.filter(([key, value]) => connectedObjectKeys.has(key)),
      Object.fromEntries,
    ])(doc.solutionModel.nodes);

    doc.connectedSolutionObjects = connectedSolutionObjects;
    doc.solutionModelTotalFlowObjectsCount = solutionModelTotalFlowObjectsCount;
    doc.solutionConnectorProps = getSolutionConnectorProps(doc);
  }

  //initialize workingModelErrors
  doc.workingModelErrors = {
    objectMistakes: [],
    flowToMistakes: [],
    objectMistakeCount: 0,
    missingObjectsCount: 0,
    flowMistakeCount: 0,
    connectorMistakeCount: 0,
    missingConnectorCount: 0,
    solutionSignalCount: 0,
  };

  dispatch(documentSlice.actions.modelLoaded({ doc, fileInfo }));

  dispatch(UndoActions.clearHistory());
};

const isTooCloseToAnother = (x, y, getState) => {
  const maxAcceptableDistanceSquared = 40 * 40;
  const existingNodes = getState().document.present.workingModel.nodes;
  let isTooClose = false;

  for (const node in existingNodes) {
    const ex = existingNodes[node].position.x;
    const ey = existingNodes[node].position.y;
    if (
      Math.pow(ex - x, 2) + Math.pow(ey - y, 2) <
      maxAcceptableDistanceSquared
    ) {
      isTooClose = true;
      break;
    }
  }
  return isTooClose;
};

export const getNewLabelCoords = (x, y, getState) => {
  while (isTooCloseToAnother(x, y, getState)) {
    const radians = Math.random() * 2 * Math.PI;
    const radius = 100; //avoidance relocation distance
    x += radius * Math.cos(radians);
    y += radius * Math.sin(radians);
    //unlikely that this alg will ever each canvas edge under normal usage,
    //but if it does, can just start stacking near last placement point
    if (x <= 0) {
      x = 100;
      break;
    } else if (y <= 0) {
      y = 100;
      break;
    }
  }
  return { x, y };
};

export const createLabel = () => (dispatch, getState) => {
  const { viewportSize, offset, scale } = getState().canvas;
  // start with center of canvas
  let x = (viewportSize.x / 2 + offset.x) / scale;
  let y = (viewportSize.y / 2 + offset.y) / scale;
  // offset the label it if it is too close to another
  ({ x, y } = getNewLabelCoords(x, y, getState));
  dispatch(documentSlice.actions.createAndPlaceLabel({ x, y }));
};

export const updateAnchorData = ({ label, data }) => (dispatch, getState) => {
  const { arrowAnchors } = getState().document.present;
  if (!arrowAnchors || !lodash.isEqual(data, arrowAnchors)) {
    dispatch(documentSlice.actions.updateAnchorDataImpl({ label, data }));
  }
};

export const createModel = ({ name, instructions }) => (dispatch, getState) => {
  const editMode = getState().canvas.defaultEditMode;
  const modelType = getState().canvas.defaultModelType;
  const doc = {
    type: modelType,
    mode: 'creation',
    language: 'en',
    uuid: v4(),
    name: name,
    creator: '',
    savedDateTime: '',
    workingModel: { nodes: {}, signals: [] },
    editMode: editMode,
    labels: {},
    allLabelBankItems: [],
    labelOrder: 'alpha',
    instructionHeader: name,
    instructions: instructions,
    initialModel: { nodes: {}, signals: [] },
    solutionModel: { nodes: {}, signals: [] },
    duplicateName: false,
  };
  const fileInfo = { source: 'new', name: 'Model' };
  // initialize labelBankItems and allLabelBankItems
  // let [model, ...err] = Diagram.fromJsonString(doc);

  // calculate arrow positions
  Arrows.init(doc);

  dispatch(documentSlice.actions.modelLoaded({ doc, fileInfo }));
  dispatch(UndoActions.clearHistory());
};
